/**
 * 🛡️ RateLimiterIndicator.jsx - Indicador Compacto del Rate Limiter para Sidebar
 * 
 * Versión compacta que muestra el estado del rate limiter junto al perfil de usuario.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, Pause } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';

const RateLimiterIndicator = ({ visible = true }) => {
    const { currentUser } = useAuth();
    const { socket } = useSocket();

    const [status, setStatus] = useState('idle'); // idle, throttling, blocked, cooldown
    const [stats, setStats] = useState({
        usage: {
            perMinute: { current: 0, max: 30 }
        }
    });
    const [showTooltip, setShowTooltip] = useState(false);

    // Escuchar eventos de Socket.IO
    useEffect(() => {
        if (!socket || !currentUser) return;

        let throttleTimeoutId = null;
        let blockedTimeoutId = null;

        const handleThrottling = (data) => {
            if (throttleTimeoutId) clearTimeout(throttleTimeoutId);
            setStatus('throttling');
            throttleTimeoutId = setTimeout(() => {
                setStatus(prev => prev === 'throttling' ? 'idle' : prev);
            }, 5000);
        };

        const handleBlocked = (data) => {
            if (blockedTimeoutId) clearTimeout(blockedTimeoutId);
            setStatus('blocked');
            blockedTimeoutId = setTimeout(() => {
                setStatus('cooldown');
                setTimeout(() => setStatus('idle'), 5000);
            }, 10000);
        };

        const handleStats = (data) => {
            if (data?.usage?.perMinute) {
                setStats(data);
                if (data.inCooldown && status === 'idle') {
                    setStatus('cooldown');
                } else if (!data.inCooldown && status === 'cooldown') {
                    setStatus('idle');
                }
            }
        };

        socket.on('rate_limiter_throttling', handleThrottling);
        socket.on('rate_limiter_blocked', handleBlocked);
        socket.on('rate_limiter_stats', handleStats);

        return () => {
            if (throttleTimeoutId) clearTimeout(throttleTimeoutId);
            if (blockedTimeoutId) clearTimeout(blockedTimeoutId);
            socket.off('rate_limiter_throttling', handleThrottling);
            socket.off('rate_limiter_blocked', handleBlocked);
            socket.off('rate_limiter_stats', handleStats);
        };
    }, [socket, currentUser, status]);

    // Determinar color e icono según estado
    const getStatusStyle = useCallback(() => {
        const current = stats.usage?.perMinute?.current || 0;
        const max = stats.usage?.perMinute?.max || 30;
        const percentage = (current / max) * 100;

        switch (status) {
            case 'throttling':
                return { color: 'text-yellow-500', bgColor: 'bg-yellow-500/20', icon: Pause };
            case 'blocked':
                return { color: 'text-red-500', bgColor: 'bg-red-500/20', icon: AlertTriangle };
            case 'cooldown':
                return { color: 'text-orange-500', bgColor: 'bg-orange-500/20', icon: Shield };
            default:
                if (percentage >= 80) return { color: 'text-yellow-500', bgColor: 'bg-yellow-500/20', icon: Shield };
                if (percentage >= 50) return { color: 'text-cyan-500', bgColor: 'bg-cyan-500/20', icon: Shield };
                return { color: 'text-green-500', bgColor: 'bg-green-500/20', icon: Shield };
        }
    }, [status, stats]);

    if (!visible) return null;

    const style = getStatusStyle();
    const Icon = style.icon;
    const current = stats.usage?.perMinute?.current || 0;
    const max = stats.usage?.perMinute?.max || 30;

    return (
        <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {/* Indicador compacto */}
            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full ${style.bgColor} cursor-pointer transition-all hover:scale-105`}>
                <Icon size={12} className={style.color} />
                <span className={`text-[10px] font-bold ${style.color}`}>
                    {current}/{max}
                </span>
            </div>

            {/* Tooltip */}
            {showTooltip && (
                <div role="tooltip" className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50">
                    <div className="font-semibold mb-1">Rate Limiter Anti-Ban</div>
                    <div className="text-gray-300">
                        {status === 'idle' && 'Estado: Normal ✅'}
                        {status === 'throttling' && 'Estado: Throttling ⏸️'}
                        {status === 'blocked' && 'Estado: Bloqueado ⛔'}
                        {status === 'cooldown' && 'Estado: Cooling down 🧊'}
                    </div>
                    <div className="text-gray-400 mt-1">
                        {current} de {max} mensajes/min
                    </div>
                    {/* Flecha del tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                </div>
            )}
        </div>
    );
};

export default RateLimiterIndicator;
